<template lang="pug">
.tc(ref="rootRef")
  .tc-card(:class="{ 'is-resetting': resetting }" aria-hidden="true")
    .tc-body
      .tc-top
        .tc-team
          span.tc-team-mark A
          span.tc-team-name Acme Studio
          span.tc-role {{ t('org.roles.owner') }}
        .tc-balance
          span.tc-balance-label {{ t('org.landing.demo.balance') }}
          span.tc-balance-value(:class="{ 'is-dropping': balanceDropping }") €{{ balanceText }}
      .tc-tabs
        span.tc-tab(v-for="tab in tabs" :key="tab" :class="{ 'is-active': tab === 'overview' }") {{ t(`org.orgPage.tabs.${tab}`) }}
      .tc-toolbar
        span.tc-btn.is-primary(ref="inviteBtnRef" :class="{ 'is-pressed': pressed === 'invite' }")
          Icon(name="lucide:mail" aria-hidden="true")
          span {{ t('org.landing.demo.invite') }}
        span.tc-btn(ref="activateBtnRef" :class="{ 'is-pressed': pressed === 'activate', 'is-busy': activating }")
          Icon.tc-btn-icon(name="lucide:refresh-cw" aria-hidden="true")
          span {{ t('org.landing.demo.activate') }}
        span.tc-count(:class="{ 'is-on': checkedCount > 0 }") {{ checkedCount }}
      .tc-table
        .tc-row.is-head
          span.tc-check(ref="selectAllRef" :class="{ 'is-on': allChecked }")
            Icon(name="lucide:check" aria-hidden="true")
          span {{ t('org.orgPage.members.table.email') }}
          span {{ t('org.orgPage.members.table.role') }}
          span {{ t('org.orgPage.members.table.status') }}
          span.tc-col-expire {{ t('org.orgPage.members.table.expire') }}
          span.tc-col-remaining {{ t('org.landing.demo.col_remaining') }}
        .tc-rows
          .tc-row(v-for="(row, index) in rows" :key="row.email" :class="{ 'is-in': row.visible, 'is-checked': row.checked }" :style="{ '--i': index }")
            span.tc-check(:class="{ 'is-on': row.checked }")
              Icon(name="lucide:check" aria-hidden="true")
            span.tc-email {{ row.email }}
            span.tc-muted {{ t('org.roles.member') }}
            span.tc-status(:class="row.joined ? 'is-active' : 'is-invited'")
              i.tc-status-dot
              | {{ row.joined ? t('org.memberStatus.active') : t('org.landing.demo.status_invited') }}
            span.tc-col-expire.tc-expire(:class="{ 'is-on': row.active }") {{ row.active ? expireText : '—' }}
            span.tc-col-remaining.tc-muted(:class="{ 'is-on': row.active }") {{ row.active ? t('org.landing.demo.remaining', { value: '200 GB' }) : t('org.orgPage.members.service.noActivatedService') }}
          p.tc-empty(:class="{ 'is-on': !anyVisible }") {{ t('org.landing.demo.empty') }}
      .tc-audit
        .tc-audit-head
          i.tc-live-dot
          span {{ t('org.landing.demo.audit_title') }}
        ul.tc-audit-list
          li.tc-audit-line(v-for="line in auditLines" :key="line.id")
            span.tc-audit-time {{ line.time }}
            span.tc-audit-text {{ line.text }}
    .tc-toast(:class="{ 'is-on': toast }")
      Icon(name="lucide:check-circle" aria-hidden="true")
      span {{ toast }}
    .tc-cursor(:class="{ 'is-on': cursorVisible, 'is-click': clicking }" :style="cursorStyle")
      svg(viewBox="0 0 24 24" width="22" height="22")
        path(d="M5 3l14 8.5-6.2 1.6L16 20l-2.6 1.2-3.2-6.9L5 19z" fill="#fff" stroke="#111" stroke-width="1.4" stroke-linejoin="round")
  ol.tc-steps
    li.tc-step(v-for="step in stepOrder" :key="step" :class="{ 'is-active': currentStep === step, 'is-done': stepDone(step) }")
      i.tc-step-dot
      span {{ t(`org.landing.demo.steps.${step}`) }}
</template>

<script setup lang="ts">
/**
 * Plays the core Teams workflow on a loop: invite members → they join → select all →
 * batch activate → the balance drops and the audit trail records every step.
 * Purely presentational; the copy comes from the same locale keys as the real dashboard.
 */
const { t } = useI18n()

const tabs = ['overview', 'invites', 'requests', 'settings', 'audit', 'finance'] as const
const stepOrder = ['invite', 'activate', 'audit'] as const
type DemoStep = typeof stepOrder[number]

const START_BALANCE = 420
const END_BALANCE = 372
const LOOP_MS = 12300

interface DemoRow {
  email: string
  name: string
  visible: boolean
  joined: boolean
  checked: boolean
  active: boolean
}

const makeRows = (): DemoRow[] => [
  { email: 'alice@acme.com', name: 'alice', visible: false, joined: false, checked: false, active: false },
  { email: 'ben@acme.com', name: 'ben', visible: false, joined: false, checked: false, active: false },
  { email: 'chloe@acme.com', name: 'chloe', visible: false, joined: false, checked: false, active: false },
]

const rootRef = ref<HTMLElement>()
const inviteBtnRef = ref<HTMLElement>()
const activateBtnRef = ref<HTMLElement>()
const selectAllRef = ref<HTMLElement>()

const rows = ref<DemoRow[]>(makeRows())
const allChecked = ref(false)
const balance = ref(START_BALANCE)
const balanceDropping = ref(false)
const auditLines = ref<{ id: number, time: string, text: string }[]>([])
const toast = ref('')
const pressed = ref<'' | 'invite' | 'activate'>('')
const activating = ref(false)
const currentStep = ref<DemoStep | null>(null)
const resetting = ref(false)
const cursorVisible = ref(false)
const clicking = ref(false)
const cursorX = ref(0)
const cursorY = ref(0)
const expireText = ref('')

const anyVisible = computed(() => rows.value.some(row => row.visible))
const checkedCount = computed(() => rows.value.filter(row => row.checked).length)
const balanceText = computed(() => new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(balance.value))
const cursorStyle = computed(() => ({ '--x': `${cursorX.value}px`, '--y': `${cursorY.value}px` }))

const stepDone = (step: DemoStep) => {
  if (!currentStep.value) return false
  return stepOrder.indexOf(step) < stepOrder.indexOf(currentStep.value)
}

const { revealed, reducedMotion } = useRevealOnce(rootRef, { threshold: 0.3 })

let timers: ReturnType<typeof setTimeout>[] = []
let auditId = 0
let balanceFrame: number | null = null
let playing = false

const at = (ms: number, run: () => void) => {
  timers.push(setTimeout(run, ms))
}

function clockNow() {
  const now = new Date()
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
}

function pushAudit(text: string) {
  auditLines.value = [...auditLines.value.slice(-2), { id: ++auditId, time: clockNow(), text }]
}

function moveCursorTo(target?: HTMLElement) {
  const root = rootRef.value
  if (!root || !target) return
  const rootRect = root.getBoundingClientRect()
  const rect = target.getBoundingClientRect()
  cursorX.value = rect.left - rootRect.left + rect.width * 0.55
  cursorY.value = rect.top - rootRect.top + rect.height * 0.6
  cursorVisible.value = true
}

function click(target: '' | 'invite' | 'activate') {
  clicking.value = true
  pressed.value = target
  at(170, () => {
    clicking.value = false
  })
  at(260, () => {
    pressed.value = ''
  })
}

function tweenBalance(from: number, to: number, durationMs: number) {
  if (balanceFrame) cancelAnimationFrame(balanceFrame)
  const startedAt = performance.now()
  balanceDropping.value = true
  const frame = (now: number) => {
    const progress = Math.min(1, (now - startedAt) / durationMs)
    const eased = 1 - (1 - progress) ** 3
    balance.value = from + (to - from) * eased
    if (progress < 1) {
      balanceFrame = requestAnimationFrame(frame)
    } else {
      balanceFrame = null
      balanceDropping.value = false
    }
  }
  balanceFrame = requestAnimationFrame(frame)
  // Background tabs stop animation frames; pin the final value so the state machine stays consistent.
  at(durationMs + 50, () => {
    if (balanceFrame) cancelAnimationFrame(balanceFrame)
    balanceFrame = null
    balance.value = to
    balanceDropping.value = false
  })
}

function resetState() {
  rows.value = makeRows()
  allChecked.value = false
  balance.value = START_BALANCE
  balanceDropping.value = false
  auditLines.value = []
  toast.value = ''
  pressed.value = ''
  activating.value = false
  currentStep.value = null
  clicking.value = false
  cursorVisible.value = false
  if (balanceFrame) {
    cancelAnimationFrame(balanceFrame)
    balanceFrame = null
  }
}

function clearTimers() {
  timers.forEach(timer => clearTimeout(timer))
  timers = []
}

function showFinalState() {
  rows.value = makeRows().map(row => ({ ...row, visible: true, joined: true, checked: true, active: true }))
  allChecked.value = true
  balance.value = END_BALANCE
  pushAudit(t('org.landing.demo.audit_invited', { actor: 'owner', n: 3 }))
  pushAudit(t('org.landing.demo.audit_joined', { name: 'chloe' }))
  pushAudit(t('org.landing.demo.audit_activated', { actor: 'owner', n: 3 }))
  currentStep.value = 'audit'
}

function play() {
  clearTimers()
  resetState()
  resetting.value = false
  playing = true

  at(300, () => moveCursorTo(inviteBtnRef.value))
  at(950, () => {
    click('invite')
    currentStep.value = 'invite'
  })
  rows.value.forEach((_, index) => at(1150 + index * 200, () => {
    rows.value[index]!.visible = true
  }))
  at(1250, () => {
    toast.value = t('org.landing.demo.toast_invited')
    pushAudit(t('org.landing.demo.audit_invited', { actor: 'owner', n: 3 }))
  })
  at(3000, () => {
    toast.value = ''
  })
  rows.value.forEach((row, index) => at(3200 + index * 400, () => {
    rows.value[index]!.joined = true
    pushAudit(t('org.landing.demo.audit_joined', { name: row.name }))
  }))
  at(4700, () => moveCursorTo(selectAllRef.value))
  at(5300, () => {
    click('')
    allChecked.value = true
  })
  rows.value.forEach((_, index) => at(5400 + index * 110, () => {
    rows.value[index]!.checked = true
  }))
  at(5950, () => moveCursorTo(activateBtnRef.value))
  at(6550, () => {
    click('activate')
    activating.value = true
    currentStep.value = 'activate'
  })
  at(7350, () => {
    activating.value = false
    tweenBalance(START_BALANCE, END_BALANCE, 900)
  })
  rows.value.forEach((_, index) => at(7350 + index * 220, () => {
    rows.value[index]!.active = true
  }))
  at(7500, () => {
    toast.value = t('org.landing.demo.toast_activated')
    pushAudit(t('org.landing.demo.audit_activated', { actor: 'owner', n: 3 }))
  })
  at(8000, () => {
    currentStep.value = 'audit'
  })
  at(9500, () => {
    toast.value = ''
  })
  at(9900, () => {
    cursorVisible.value = false
  })
  at(LOOP_MS - 500, () => {
    resetting.value = true
  })
  at(LOOP_MS, play)
}

function stop() {
  clearTimers()
  playing = false
}

function onVisibilityChange() {
  if (document.hidden) {
    stop()
  } else if (revealed.value && !reducedMotion.value && !playing) {
    play()
  }
}

watch(revealed, (isRevealed) => {
  if (!isRevealed) return
  if (reducedMotion.value) {
    showFinalState()
    return
  }
  play()
})

onMounted(() => {
  const expire = new Date()
  expire.setDate(expire.getDate() + 30)
  expireText.value = expire.toISOString().slice(0, 10)
  document.addEventListener('visibilitychange', onVisibilityChange)
})

onBeforeUnmount(() => {
  stop()
  if (balanceFrame) cancelAnimationFrame(balanceFrame)
  document.removeEventListener('visibilitychange', onVisibilityChange)
})
</script>

<style lang="postcss" scoped>
.tc {
  --tc-bg: var(--tm-surface, #0b0e0a);
  --tc-bg-2: var(--tm-surface-2, #161a15);
  --tc-text: var(--tm-text, #f5f7f1);
  --tc-copy: var(--tm-copy, #969f92);
  --tc-faint: var(--tm-faint, #626a5e);
  --tc-line: var(--tm-line, rgba(255, 255, 255, 0.08));
  --tc-line-strong: var(--tm-line-strong, rgba(255, 255, 255, 0.16));
  --tc-green: var(--tm-green, #7ed321);
  --tc-green-soft: var(--tm-green-soft, rgba(126, 211, 33, 0.16));
  --tc-on-green: var(--tm-on-green, #071006);
  --tc-amber: #e8b04a;
  --tc-ease: cubic-bezier(0.16, 1, 0.3, 1);

  position: relative;
  width: 100%;
  container-type: inline-size;
  font-size: 0.78rem;
  color: var(--tc-text);
}

.tc-card {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--tc-line);
  border-radius: 14px;
  background: var(--tc-bg);
  box-shadow:
    0 30px 80px -30px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(126, 211, 33, 0.04);
}

.tc-body {
  transition: opacity 0.45s var(--tc-ease);
}

.tc-card.is-resetting .tc-body {
  opacity: 0;
}

/* ---------- top bar ---------- */

.tc-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid var(--tc-line);
}

.tc-team {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;
}

.tc-team-mark {
  display: inline-grid;
  place-items: center;
  flex: none;
  width: 1.7rem;
  height: 1.7rem;
  border-radius: 8px;
  background: var(--tc-green);
  color: var(--tc-on-green);
  font-size: 0.8rem;
  font-weight: 800;
}

.tc-team-name {
  font-size: 0.86rem;
  font-weight: 700;
  white-space: nowrap;
}

.tc-role {
  padding: 0.18rem 0.5rem;
  border: 1px solid var(--tc-line-strong);
  border-radius: 999px;
  color: var(--tc-copy);
  font-size: 0.66rem;
  font-weight: 700;
  white-space: nowrap;
}

.tc-balance {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  white-space: nowrap;
}

.tc-balance-label {
  color: var(--tc-faint);
  font-size: 0.68rem;
  font-weight: 600;
}

.tc-balance-value {
  font-size: 0.95rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  transition: color 0.3s var(--tc-ease);
}

.tc-balance-value.is-dropping {
  color: var(--tc-green);
}

/* ---------- tabs ---------- */

.tc-tabs {
  display: flex;
  gap: 0.15rem;
  padding: 0.35rem 0.6rem 0;
  border-bottom: 1px solid var(--tc-line);
  overflow: hidden;
}

.tc-tab {
  position: relative;
  padding: 0.5rem 0.55rem 0.6rem;
  color: var(--tc-faint);
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
}

.tc-tab.is-active {
  color: var(--tc-text);
}

.tc-tab.is-active::after {
  content: '';
  position: absolute;
  right: 0.55rem;
  bottom: -1px;
  left: 0.55rem;
  height: 2px;
  border-radius: 2px;
  background: var(--tc-green);
}

/* ---------- toolbar ---------- */

.tc-toolbar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
}

.tc-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 1.9rem;
  padding: 0 0.7rem;
  border: 1px solid var(--tc-line-strong);
  border-radius: 8px;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  transition:
    transform 0.18s var(--tc-ease),
    background-color 0.25s var(--tc-ease),
    border-color 0.25s var(--tc-ease);
}

.tc-btn :deep(svg) {
  width: 0.85rem;
  height: 0.85rem;
}

.tc-btn.is-primary {
  border-color: transparent;
  background: var(--tc-green);
  color: var(--tc-on-green);
}

.tc-btn.is-pressed {
  transform: scale(0.94);
}

.tc-btn:not(.is-primary).is-pressed {
  background: var(--tc-green-soft);
  border-color: var(--tc-green);
}

.tc-btn.is-busy .tc-btn-icon {
  animation: tc-spin 0.8s linear infinite;
}

@keyframes tc-spin {
  to {
    transform: rotate(360deg);
  }
}

.tc-count {
  display: inline-grid;
  place-items: center;
  min-width: 1.4rem;
  height: 1.4rem;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: var(--tc-bg-2);
  color: var(--tc-faint);
  font-size: 0.66rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  opacity: 0;
  transform: scale(0.7);
  transition:
    opacity 0.25s var(--tc-ease),
    transform 0.25s var(--tc-ease);
}

.tc-count.is-on {
  opacity: 1;
  transform: scale(1);
  color: var(--tc-text);
}

/* ---------- table ---------- */

.tc-table {
  padding: 0 0.5rem;
}

.tc-rows {
  position: relative;
  display: grid;
  align-content: start;
  gap: 0.3rem;
  min-height: calc(3 * 2.35rem + 2 * 0.3rem);
}

.tc-row {
  display: grid;
  grid-template-columns: 1.6rem minmax(0, 1.5fr) minmax(0, 0.7fr) minmax(0, 0.9fr) minmax(0, 1fr) minmax(0, 1.1fr);
  align-items: center;
  gap: 0.5rem;
  min-height: 2.35rem;
  padding: 0 0.5rem;
  border-radius: 8px;
  white-space: nowrap;
}

.tc-row > span {
  overflow: hidden;
  text-overflow: ellipsis;
}

.tc-row.is-head {
  min-height: 2rem;
  color: var(--tc-faint);
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.tc-rows .tc-row {
  opacity: 0;
  transform: translate3d(0, 0.6rem, 0);
  transition:
    opacity 0.5s var(--tc-ease),
    transform 0.5s var(--tc-ease),
    background-color 0.3s var(--tc-ease);
}

.tc-rows .tc-row.is-in {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}

.tc-rows .tc-row.is-checked {
  background: var(--tc-green-soft);
}

.tc-check {
  display: inline-grid;
  place-items: center;
  width: 1rem;
  height: 1rem;
  border: 1px solid var(--tc-line-strong);
  border-radius: 4px;
  color: var(--tc-on-green);
  transition:
    background-color 0.2s var(--tc-ease),
    border-color 0.2s var(--tc-ease);
}

.tc-check :deep(svg) {
  width: 0.7rem;
  height: 0.7rem;
  opacity: 0;
  transform: scale(0.5);
  transition:
    opacity 0.2s var(--tc-ease),
    transform 0.2s var(--tc-ease);
}

.tc-check.is-on {
  border-color: var(--tc-green);
  background: var(--tc-green);
}

.tc-check.is-on :deep(svg) {
  opacity: 1;
  transform: scale(1);
}

.tc-email {
  font-weight: 600;
}

.tc-muted {
  color: var(--tc-copy);
}

.tc-status {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: 700;
  transition: color 0.3s var(--tc-ease);
}

.tc-status-dot {
  flex: none;
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 999px;
  background: currentColor;
  transition: background-color 0.3s var(--tc-ease);
}

.tc-status.is-invited {
  color: var(--tc-amber);
}

.tc-status.is-active {
  color: var(--tc-green);
}

.tc-expire {
  color: var(--tc-faint);
  font-variant-numeric: tabular-nums;
  transition: color 0.3s var(--tc-ease);
}

.tc-expire.is-on {
  color: var(--tc-text);
}

.tc-col-remaining.is-on {
  color: var(--tc-text);
}

.tc-empty {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  margin: 0;
  color: var(--tc-faint);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s var(--tc-ease);
}

.tc-empty.is-on {
  opacity: 1;
}

/* ---------- audit ---------- */

.tc-audit {
  margin-top: 0.4rem;
  padding: 0.7rem 1rem 0.85rem;
  border-top: 1px solid var(--tc-line);
  background: var(--tc-bg-2);
}

.tc-audit-head {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  color: var(--tc-faint);
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.tc-live-dot {
  width: 0.45rem;
  height: 0.45rem;
  border-radius: 999px;
  background: var(--tc-green);
  box-shadow: 0 0 0 0 rgba(126, 211, 33, 0.5);
  animation: tc-pulse 2s ease-out infinite;
}

@keyframes tc-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(126, 211, 33, 0.5);
  }

  70% {
    box-shadow: 0 0 0 0.45rem rgba(126, 211, 33, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(126, 211, 33, 0);
  }
}

.tc-audit-list {
  display: grid;
  gap: 0.25rem;
  min-height: calc(3 * 1.35rem + 0.5rem);
  margin: 0.5rem 0 0;
  padding: 0;
  list-style: none;
}

.tc-audit-line {
  display: flex;
  gap: 0.6rem;
  line-height: 1.35rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  animation: tc-line-in 0.45s var(--tc-ease);
}

@keyframes tc-line-in {
  from {
    opacity: 0;
    transform: translate3d(0, 0.4rem, 0);
  }

  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

.tc-audit-time {
  flex: none;
  color: var(--tc-faint);
  font-variant-numeric: tabular-nums;
}

.tc-audit-text {
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--tc-copy);
}

/* ---------- toast + cursor ---------- */

.tc-toast {
  position: absolute;
  right: 1rem;
  bottom: 1rem;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.55rem 0.8rem;
  border: 1px solid var(--tc-line-strong);
  border-radius: 10px;
  background: var(--tc-bg-2);
  box-shadow: 0 18px 40px -18px rgba(0, 0, 0, 0.7);
  color: var(--tc-text);
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
  opacity: 0;
  transform: translate3d(0, 0.6rem, 0);
  transition:
    opacity 0.35s var(--tc-ease),
    transform 0.35s var(--tc-ease);
}

.tc-toast :deep(svg) {
  width: 0.9rem;
  height: 0.9rem;
  color: var(--tc-green);
}

.tc-toast.is-on {
  opacity: 1;
  transform: translate3d(0, 0, 0);
}

.tc-cursor {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 3;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.45));
  opacity: 0;
  transform: translate3d(var(--x), var(--y), 0) scale(1);
  transform-origin: 5px 3px;
  pointer-events: none;
  transition:
    transform 0.7s var(--tc-ease),
    opacity 0.3s var(--tc-ease);
}

.tc-cursor.is-on {
  opacity: 1;
}

.tc-cursor.is-click {
  transform: translate3d(var(--x), var(--y), 0) scale(0.82);
  transition-duration: 0.12s;
}

/* ---------- steps legend ---------- */

.tc-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  margin: 1rem 0 0;
  padding: 0;
  list-style: none;
}

.tc-step {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  color: var(--tc-faint);
  font-size: 0.72rem;
  font-weight: 700;
  transition:
    color 0.3s var(--tc-ease),
    background-color 0.3s var(--tc-ease);
}

.tc-step + .tc-step::before {
  content: '';
  width: 1.2rem;
  height: 1px;
  margin-right: 0.35rem;
  margin-left: -0.55rem;
  background: var(--tc-line-strong);
}

.tc-step-dot {
  width: 0.5rem;
  height: 0.5rem;
  border: 1px solid var(--tc-line-strong);
  border-radius: 999px;
  transition:
    background-color 0.3s var(--tc-ease),
    border-color 0.3s var(--tc-ease),
    box-shadow 0.3s var(--tc-ease);
}

.tc-step.is-active {
  color: var(--tc-text);
  background: var(--tc-green-soft);
}

.tc-step.is-active .tc-step-dot,
.tc-step.is-done .tc-step-dot {
  border-color: var(--tc-green);
  background: var(--tc-green);
}

.tc-step.is-active .tc-step-dot {
  box-shadow: 0 0 0 0.25rem rgba(126, 211, 33, 0.2);
}

.tc-step.is-done {
  color: var(--tc-copy);
}

@container (max-width: 640px) {
  .tc-card {
    font-size: 0.72rem;
  }

  .tc-tabs {
    padding-inline: 0.3rem;
  }

  .tc-tab {
    padding-inline: 0.4rem;
    font-size: 0.66rem;
  }

  .tc-row {
    grid-template-columns: 1.4rem minmax(0, 1.6fr) minmax(0, 0.8fr) minmax(0, 1fr);
  }

  .tc-col-remaining,
  .tc-col-expire {
    display: none;
  }

  .tc-balance-label {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tc-body,
  .tc-rows .tc-row,
  .tc-toast,
  .tc-cursor,
  .tc-count,
  .tc-check,
  .tc-check :deep(svg) {
    transition: none;
  }

  .tc-live-dot,
  .tc-audit-line,
  .tc-btn.is-busy .tc-btn-icon {
    animation: none;
  }

  .tc-cursor {
    display: none;
  }
}
</style>
